import { apiClient } from '@app/api';
import { DEFAULT_TIME_RANGE_MS, SEGMENTS_REFETCH_INTERVAL_MS } from '../constants';
import { Segment } from '@mi-front/core';
import { useQuery } from '@tanstack/react-query';

interface Range {
  readonly from: string | number;
  readonly to: string | number;
}

interface UseLatestSegmentsParams {
  readonly stationId: string;
  readonly enabled?: boolean;
  readonly range?: Range;
}

/**
 * Hook to fetch segments for a station using React Query.
 * If no range is provided, defaults to [-6 minutes, now].
 *
 * @param params - Configuration object
 * @param params.stationId - The ID of the station to fetch segments for
 * @param params.enabled - Whether the fetch should be enabled (defaults to true)
 * @param params.range - Optional time range to fetch segments for. If not provided, defaults to [-6 minutes, now]
 */
export function useLatestSegments(params: UseLatestSegmentsParams) {
  const { stationId, enabled, range } = params;
  // Calculate time range: use provided range or default to [-6 minutes, now]
  const timeRange = (() => {
    if (range) {
      return {
        from: new Date(range.from).valueOf(),
        to: new Date(range.to).valueOf(),
      };
    }

    const now = Date.now();
    return {
      from: now - DEFAULT_TIME_RANGE_MS,
      to: now,
    };
  })();

  const query = useQuery({
    queryKey: useLatestSegments.queryKey(stationId),
    queryFn: () => {
      return apiClient.segments
        .fetchRange(stationId, timeRange.from, timeRange.to)
        .then((res) => res as Segment[]);
    },
    enabled: enabled ?? true,
    staleTime: 0, // Always consider data stale to allow refetching
    refetchInterval: SEGMENTS_REFETCH_INTERVAL_MS,
    refetchIntervalInBackground: true, // Continue refetching even when tab is in background
  });

  return {
    data: query.data ?? [],
    isLoading: query.data ? false : query.isLoading || !enabled,
    error: query.error as Error | null,
    refetch: query.refetch,
    timeRange: {
      from: timeRange.from,
      to: timeRange.to,
    },
  };
}

useLatestSegments.queryKey = (stationId: string) => ['latest-segments', stationId] as const;
